import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Inbox } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { IntakeOverviewTab } from "@/components/org/intake/IntakeOverviewTab";
import { IntakeModelsTab } from "@/components/org/intake/IntakeModelsTab";
import { QRCodesTab } from "@/components/org/intake/QRCodesTab";
import { IntakeSettingsTab } from "@/components/org/intake/IntakeSettingsTab";
import { IntakeAnalyticsTab } from "@/components/org/intake/IntakeAnalyticsTab";
import { ConnectionsSideNav } from "@/components/org/connections/ConnectionsSideNav";
import { CT } from "@/lib/connectionsTheme";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "models", label: "Intake Models" },
  { key: "qr", label: "QR Codes" },
  { key: "settings", label: "Settings" },
  { key: "analytics", label: "Analytics" },
];

export default function OrgCustomerIntake() {
  const { API } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TABS.some((t) => t.key === searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const [tab, setTab] = useState(initialTab);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");

  const changeTab = (key) => {
    setTab(key);
    setSearchParams(key === "overview" ? {} : { tab: key });
  };

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/org/intake/overview`, { withCredentials: true });
      setOverview(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  return (
    <div className={CT.page} data-testid="org-customer-intake-page">
      <div className="flex flex-col gap-6 xl:flex-row">
        <ConnectionsSideNav activeKey="customer_intake" />

        <div className="min-w-0 flex-1 space-y-6">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-500/15">
              <Inbox size={20} className="text-blue-600 dark:text-blue-400" strokeWidth={1.8} />
            </span>
            <div>
              <h1 className={CT.h1}>Customer Intake</h1>
              <p className={`mt-1 ${CT.textSecondary}`}>Design what customers submit, verify their identity, and distribute stable QR codes &amp; links — all routed into Front Desk.</p>
            </div>
          </div>

          {error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400" data-testid="customer-intake-error">{error}</p>}

          <div className="flex flex-wrap gap-5 border-b border-slate-200 dark:border-white/10" data-testid="customer-intake-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => changeTab(t.key)}
                data-testid={`customer-intake-tab-${t.key}`}
                className={`-mb-px border-b-2 px-1 pb-3 text-sm font-bold transition-colors duration-150 ${
                  tab === t.key ? CT.tabActive : CT.tabInactive
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div data-testid={`customer-intake-tab-panel-${tab}`}>
            {tab === "overview" && <IntakeOverviewTab overview={overview} onGoToModels={() => changeTab("models")} />}
            {tab === "models" && <IntakeModelsTab canManage={!!overview?.can_manage} onModelsChanged={loadOverview} />}
            {tab === "qr" && <QRCodesTab canManage={!!overview?.can_manage} />}
            {tab === "settings" && <IntakeSettingsTab overview={overview} />}
            {tab === "analytics" && <IntakeAnalyticsTab />}
          </div>
        </div>
      </div>
    </div>
  );
}
